/**
 * Mode-switch report (AC-038/039/048/076, FP-7/FP-11): every republished / skipped / failed article with its reason,
 * the counts, and the related-refresh outcome. "Progress" while the job runs — with a "Waiting" row per article not
 * reached yet (D-5) — and "Last switch" after it. Failed articles are ALSO listed in an error alert above the table
 * (AC-076 "error list"). Articles show their title with "/{alias}" on a monospace second line (approved mock) through the live articles.
 */
import React from 'react';
import { Alert, AlertTitle, Box, Chip, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material';
// Subpath import: the ui-components root barrel is not jest-safe (import.meta via data-pickers).
import { SDataTable, type SDataTableColumnDef } from '@modules/ui-components/dialogs/SDataTable';
import type { LiveArticle } from '@modules/firebase-core';
import { CUSTOM_DOMAIN_TEST_IDS } from '@modules/custom-domains/testIds';
import { PATH_MODE_COPY, articleLabel } from '@modules/custom-domains/components/articlePathModeCopy';
import type { ArticleModeSwitchJob, ArticleModeSwitchResult } from '@modules/custom-domains/types/customDomain.types';

const copy = PATH_MODE_COPY.report;

type RowStatus = ArticleModeSwitchResult['status'] | 'waiting';
type ChipColor = 'success' | 'warning' | 'error' | 'default';
const RESULT_CHIP: Record<RowStatus, { label: string; color: ChipColor }> = {
  republished: { label: copy.chips.republished, color: 'success' },
  skipped: { label: copy.chips.skipped, color: 'warning' },
  failed: { label: copy.chips.failed, color: 'error' },
  waiting: { label: copy.chips.waiting, color: 'default' },
};

/**
 * Error-coloured TEXT on the report: `error.main` reads 3.9:1 on the dark table surface (WCAG AA needs 4.5:1 for
 * 13-14 px), so dark mode uses the lighter error token (design review 2026-10-07, rendered measurement).
 */
const errorTextSx = { color: (theme: Theme) => (theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main) };

interface ReportRow {
  id: string;
  /** First line: the article title, or "/{alias}" (the article id) when the title is unknown. */
  label: string;
  /** Second, monospace line under a known title (approved mock): "/{alias}". */
  aliasLine: string | null;
  status: RowStatus;
  note: string;
}

/** The address a skipped article keeps: the server note carries it; the article host is the fallback. */
const staleUrlOf = (result: ArticleModeSwitchResult, articleHost: string): string =>
  /canonical stays (\S+)/.exec(result.note ?? '')?.[1] ?? `https://${articleHost}/${result.alias}`;

function noteOf(result: ArticleModeSwitchResult, articleHost: string, siteHost: string): string {
  if (result.status === 'failed') return result.error ?? '';
  if (result.status === 'skipped') {
    return result.reason === 'pending edits' ? copy.skippedNote(staleUrlOf(result, articleHost)) : result.note ?? '';
  }
  return result.warnings?.length ? copy.rootRelativeNote(result.warnings.length, siteHost) : '';
}

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Sizes are minimums (the table stretches to its container): 540 px in total keeps all three columns visible at the
// md breakpoint (900 px viewport) instead of scrolling the Note column out of view (design review 2026-10-07).
const COLUMNS: SDataTableColumnDef<ReportRow>[] = [
  {
    accessorKey: 'label',
    header: copy.columns.article,
    size: 200,
    Cell: ({ row }) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{row.original.label}</Typography>
        {row.original.aliasLine && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
            {row.original.aliasLine}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    accessorKey: 'status',
    header: copy.columns.result,
    size: 110,
    Cell: ({ row }) => {
      const chip = RESULT_CHIP[row.original.status];
      return <Chip size="small" variant="outlined" color={chip.color} label={chip.label} sx={chip.color === 'error' ? errorTextSx : undefined} />;
    },
  },
  {
    accessorKey: 'note',
    header: copy.columns.note,
    size: 228,
    Cell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{row.original.note || '—'}</Typography>
    ),
  },
];

export interface ModeSwitchReportProps {
  job: ArticleModeSwitchJob;
  articleHost: string;
  /** Live articles: titles for the rows and the article behind a pending id. */
  liveArticles: readonly LiveArticle[];
}

export function ModeSwitchReport({ job, articleHost, liveArticles }: ModeSwitchReportProps) {
  const running = job.status === 'running';
  const siteHost = job.publicBaseUrl ? new URL(job.publicBaseUrl).host : articleHost;
  const byId = new Map(liveArticles.filter((a) => a.articleId).map((a) => [a.articleId as string, a]));
  const byAlias = new Map(liveArticles.map((a) => [a.alias, a]));
  const labelOf = (articleId: string, alias?: string): Pick<ReportRow, 'label' | 'aliasLine'> => {
    const live = byId.get(articleId) ?? (alias ? byAlias.get(alias) : undefined);
    const knownAlias = alias || live?.alias;
    if (!knownAlias) return { label: articleId, aliasLine: null };
    return live?.title ? { label: live.title, aliasLine: `/${knownAlias}` } : { label: articleLabel(knownAlias), aliasLine: null };
  };

  const count = (status: ArticleModeSwitchResult['status']) => job.results.filter((r) => r.status === status).length;
  const failed = job.results.filter((r) => r.status === 'failed');
  const done = new Set(job.results.map((r) => r.articleId));
  const waitingIds = running
    ? [...(job.inFlight?.articleIds ?? []), ...job.pending].filter((id, i, all) => !done.has(id) && all.indexOf(id) === i)
    : [];
  const waiting = running ? Math.max(waitingIds.length, job.total - job.results.length) : 0;

  const summary = [
    running ? null : formatDate(job.finishedAt),
    `${count('republished')} republished`,
    count('skipped') ? `${count('skipped')} skipped` : null,
    failed.length ? `${failed.length} failed` : null,
    waiting ? `${waiting} waiting` : null,
  ].filter(Boolean).join(' · ');

  const rows: ReportRow[] = [
    ...job.results.map((result): ReportRow => ({
      id: result.articleId,
      ...labelOf(result.articleId, result.alias),
      status: result.status,
      note: noteOf(result, articleHost, siteHost),
    })),
    ...waitingIds.map((id): ReportRow => ({ id, ...labelOf(id), status: 'waiting', note: '' })),
  ];

  const related = !running && job.relatedRefresh;
  return (
    <Box sx={{ mt: 3 }} data-testid={CUSTOM_DOMAIN_TEST_IDS.baseUrl.report}>
      {failed.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{copy.failedTitle(failed.length)}</AlertTitle>
          {failed.map((result) => {
            const live = byId.get(result.articleId) ?? byAlias.get(result.alias);
            const name = live?.title || (result.alias ? `/${result.alias}` : result.articleId);
            const error = result.error ?? '';
            return (
              <Box key={result.articleId} component="span" sx={{ display: 'block' }}>
                {`${name}: ${error}${/[.!?]$/.test(error) ? '' : '.'}`}
              </Box>
            );
          })}
          <Box component="span" sx={{ display: 'block' }}>{copy.failedTail(failed.length)}</Box>
        </Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600 }}>
          {running ? copy.progress : copy.lastSwitch}
        </Typography>
        <Typography variant="body2" color="text.secondary">{summary}</Typography>
      </Stack>
      {rows.length > 0 && (
        <SDataTable
          columns={COLUMNS}
          data={rows}
          getRowId={(row) => row.id}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enablePagination={false}
          enableSorting={false}
          enableColumnActions={false}
        />
      )}
      {related && (
        <Typography variant="body2" sx={{ mt: 1, ...(related.ok ? { color: 'text.secondary' } : errorTextSx) }}>
          {related.ok ? copy.relatedOk : job.kind === 'clear' ? copy.relatedFailedAfterClear : copy.relatedFailed}
        </Typography>
      )}
    </Box>
  );
}

export default ModeSwitchReport;
